import moment from 'moment';

// Comparison operators available for structured (numeric/date) section conditions.
const OPERATORS = {
    '==': (a, b) => a === b,
    '!=': (a, b) => a !== b,
    '<': (a, b) => a < b,
    '<=': (a, b) => a <= b,
    '>': (a, b) => a > b,
    '>=': (a, b) => a >= b
};

// Normalizes any value to a trimmed lowercase string for tolerant comparisons.
const normalizeString = (value) =>
    String(value ?? '')
        .trim()
        .toLowerCase();

// Normalizes checkbox values so booleans and their string counterparts compare equal.
const normalizeCheckboxScalar = (value) => {
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return normalizeString(value);
};

// Determines how the controlling field's value has to be interpreted (text, number, date, checkbox).
export const inferControllingType = (field, rawValue) => {
    if (field.controllingType) return field.controllingType;
    if (Array.isArray(rawValue)) return 'checkbox';
    if (rawValue instanceof Date) return 'date';

    const children = field.children ?? [];
    const hasStructuredCondition = children.some((child) => child.condition);
    const hasLinkedValues = children.some(
        (child) => Array.isArray(child.linkedValues) && child.linkedValues.length > 0
    );

    // How the branches are defined beats what the value looks like: a stored select value
    // like "2" is a text comparison, not a numeric condition.
    if (hasLinkedValues && !hasStructuredCondition) {
        return typeof rawValue === 'boolean' ? 'checkbox' : 'text';
    }

    if (typeof rawValue === 'number') return 'number';
    if (typeof rawValue === 'string') {
        if (moment(rawValue, moment.ISO_8601, true).isValid()) return 'date';
        if (rawValue.trim() !== '' && !Number.isNaN(Number(rawValue))) return 'number';
    }

    if (hasStructuredCondition) {
        const hasDateCondition = children.some((child) =>
            /^\d{4}-\d{2}-\d{2}$/.test(String(child.condition?.value ?? ''))
        );
        return hasDateCondition ? 'date' : 'number';
    }

    return 'text';
};

// Checks a numeric branch condition against the controlling value.
const matchesNumberCondition = (child, rawValue) => {
    const num = parseFloat(String(rawValue ?? ''));
    if (Number.isNaN(num)) return false;

    const cond = child.condition;
    if (!cond) return false;

    const fn = OPERATORS[cond.operator];
    return fn ? fn(num, Number(cond.value)) : false;
};

// Checks a date branch condition against the controlling value.
const matchesDateCondition = (child, rawValue) => {
    if (!rawValue) return false;

    const sourceDate =
        rawValue instanceof Date
            ? moment(rawValue)
            : moment(String(rawValue), [moment.ISO_8601, 'DD.MM.YYYY', 'YYYY-MM-DD'], true);
    if (!sourceDate.isValid()) return false;

    const cond = child.condition;
    if (!cond || !cond.value) return false;

    const targetDate = moment(String(cond.value), ['YYYY-MM-DD', 'DD.MM.YYYY', moment.ISO_8601], true);
    if (!targetDate.isValid()) return false;

    const fn = OPERATORS[cond.operator];
    return fn ? fn(sourceDate.startOf('day').valueOf(), targetDate.startOf('day').valueOf()) : false;
};

// Checks a value-based branch condition (select/checkbox options) against the controlling value.
const matchesLinkedValues = (child, rawValue, type) => {
    const linkedValues = Array.isArray(child.linkedValues) ? child.linkedValues : [];
    if (linkedValues.length === 0) return false;

    if (Array.isArray(rawValue)) {
        const normalizedSelected = rawValue.map(normalizeString);
        return linkedValues.some((value) => normalizedSelected.includes(normalizeString(value)));
    }

    if (type === 'checkbox') {
        return linkedValues.some((value) => normalizeCheckboxScalar(value) === normalizeCheckboxScalar(rawValue));
    }

    return linkedValues.some((value) => normalizeString(value) === normalizeString(rawValue));
};

// Single source of truth: is this branch of a conditional section active for the given controlling value?
export const isSectionChildVisible = (sectionField, child, rawValue) => {
    if (sectionField.sectionMode !== 'conditional' || !sectionField.dependsOnField) return true;

    const type = inferControllingType(sectionField, rawValue);
    if (type === 'number') return matchesNumberCondition(child, rawValue);
    if (type === 'date') return matchesDateCondition(child, rawValue);

    return matchesLinkedValues(child, rawValue, type);
};

// Returns the branches of a section that are currently active (all of them for non-conditional sections).
export const getVisibleSectionChildren = (sectionField, rawValue) => {
    const children = Array.isArray(sectionField.children) ? sectionField.children : [];
    if (sectionField.sectionMode !== 'conditional' || !sectionField.dependsOnField) return children;

    return children.filter((child) => isSectionChildVisible(sectionField, child, rawValue));
};

// Collects the names of every field living in a branch of a conditional section that is not
// active for the given values. Used to keep those empty values out of the submit payload.
export const getHiddenConditionalFieldNames = (fields = [], values = {}) =>
    (fields ?? []).flatMap((field) => {
        if (field?.type !== 'section') return [];

        const children = Array.isArray(field.children) ? field.children : [];
        const nested = children.flatMap((child) => getHiddenConditionalFieldNames(child.fields ?? [], values));

        if (field.sectionMode !== 'conditional' || !field.dependsOnField) return nested;

        const rawValue = values?.[field.dependsOnField];
        const hidden = children
            .filter((child) => !isSectionChildVisible(field, child, rawValue))
            .flatMap((child) => (child.fields ?? []).map((f) => f?.name).filter(Boolean));

        return [...nested, ...hidden];
    });
